import { useDebounceValue, useOnClickOutside } from '@easeltv/hooks';
import { useEffect, useRef, useState } from 'react';

import {
    ClearIcon,
    InputWrapper,
    SearchIcon,
    StyledForm,
    StyledInput,
} from './SearchBlock.styled';

/* ---------------------------------- Types --------------------------------- */

export interface DumbSearchBlockI {
    /** Method for returning to screen prior to this search screen */
    closeSearch: () => void;

    /** Boolean if user is currently on the search screen */
    isOnSearchPage: boolean;

    /** Status of the form, set to true of false */
    isOpen: boolean;

    /** Method for displaying the form */
    openSearch: () => void;

    /** String value displayed in the text box */
    searchTerm: string;

    /** Method to control changing of search term */
    setSearchTerm: (term: string) => void;
}

/* -------------------------------- Component ------------------------------- */

const DumbSearchBlock = ({
    closeSearch,
    isOnSearchPage,
    isOpen,
    openSearch,
    searchTerm,
    setSearchTerm,
}: DumbSearchBlockI) => {
    const formRef = useRef<HTMLFormElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Maintain local search value so the term passed up can be debounced
    const [rawSearchTerm, setRawSearchTerm] = useState(searchTerm);
    const debouncedSearchTerm = useDebounceValue(rawSearchTerm, 300);

    const goBack = () => {
        setRawSearchTerm('');
        closeSearch();
    };

    // Update the search term in the parent component - debounced
    useEffect(() => {
        setSearchTerm(debouncedSearchTerm);
    }, [debouncedSearchTerm, setSearchTerm]);

    // Close form and clear the search field only if not on the search page
    useOnClickOutside(formRef, () => {
        if (!isOnSearchPage) {
            setRawSearchTerm('');
            closeSearch();
        }
    });

    const doOpenSearch = () => {
        if (inputRef) {
            inputRef.current?.focus();
        }
        openSearch();
    };

    return (
        <StyledForm
            method="POST"
            onSubmit={(e) => {
                e.preventDefault();
                setSearchTerm(debouncedSearchTerm);
            }}
            open={isOpen}
            ref={formRef}
        >
            <InputWrapper>
                <StyledInput
                    aria-label="Search"
                    type="text"
                    value={rawSearchTerm}
                    ref={inputRef}
                    onChange={(e) => setRawSearchTerm(e.target.value)}
                />
                {isOpen && isOnSearchPage && (
                    <ClearIcon className="fa fa-close" onClick={goBack} />
                )}
            </InputWrapper>
            <SearchIcon className="fa fa-search" onClick={doOpenSearch} />
        </StyledForm>
    );
};

/* --------------------------------- Export --------------------------------- */

export default DumbSearchBlock;
